import { ClipboardEvent, DragEvent, KeyboardEvent, MutableRefObject, ReactNode, Ref, useState } from "react";
import { toast } from "sonner";
import { ArrowUpIcon, DotsThreeVerticalIcon, SparkleIcon, StopIcon, TrashSimpleIcon, XIcon } from "@phosphor-icons/react";
import { AppButton, AppTextarea, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@libs/zapi-ui/shadcn";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu as DsDropdownMenu,
  DropdownMenuContent as DsDropdownMenuContent,
  DropdownMenuItem as DsDropdownMenuItem,
  DropdownMenuTrigger as DsDropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea
} from "@libs/zapi-ui/ds/components/input-group";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { LeoWordmark } from "@libs/zapi-ui/ds/components/leo-wordmark";
import { KeyReturnIcon } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useDockedChatClose } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel/DockedChatContext";
import { ChatAppearanceProvider } from "./ChatAppearance";

type TClearConfirm = {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  menuLabel?: string;
  toastText?: string;
};

type TChatShellProps = {
  children?: ReactNode;
  // "leo" is the redesign's chat on the project page and the project list, drawn like the create page's Leo.
  appearance?: "default" | "leo";
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  title?: string;
  titleAdornment?: ReactNode;
  footnote?: ReactNode;
  // Takes the footnote's place while isBusy, in the "leo" appearance.
  busyNotice?: ReactNode;
  placeholder?: string;
  isBusy?: boolean;
  isComposerDisabled?: boolean;
  // The body shows a spinner and loadingLabel; the composer stays visible but disabled.
  isLoading?: boolean;
  loadingLabel?: string;
  // Lets a chat focus its message box, e.g. when a button asks the user to type their own answer.
  inputRef?: Ref<HTMLTextAreaElement>;
  headerRight?: ReactNode;
  headerEnd?: ReactNode;
  controls?: ReactNode;
  controlsLeft?: ReactNode;
  topBar?: ReactNode;
  submitLabel?: string;
  canSubmit?: boolean;
  inputExtras?: ReactNode;
  // Files pasted into the message box or dropped on the chat. A paste that also carries text
  // (copied from a document) stays a text paste.
  onPasteFiles?: (files: File[]) => void;
  onDropFiles?: (files: File[]) => void;
  hideComposer?: boolean;
  onStop?: () => void;
  stopTestId?: string;
  onClear?: () => void;
  canClear?: boolean;
  clearConfirm?: TClearConfirm;
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
  className?: string;
  bodyClassName?: string;
  // Default to English so callers that resolve their own strings (including the fixed-language
  // chats using i18n.getFixedT) are unaffected by not passing them.
  optionsMenuLabel?: string;
  stopLabel?: string;
  dockCloseLabel?: string;
  "data-testid"?: string;
  inputTestId?: string;
  sendTestId?: string;
  clearTestId?: string;
};

// Dictation input sends NBSP for line breaks; normalize once for every chat.
const normalizeDictation = (value: string) => value.replace(/\u00a0/g, "\n");

// The layout is the Project List chat panel, which is the UI reference for INV-2361.
export function ChatShell({
  children,
  appearance = "default",
  input,
  onInputChange,
  onSubmit,
  title = "Chat",
  titleAdornment,
  footnote,
  busyNotice,
  placeholder = "Type a message to AI...",
  isBusy = false,
  isComposerDisabled: isComposerDisabledProp = false,
  isLoading = false,
  loadingLabel = "Loading Messages...",
  inputRef,
  headerRight,
  headerEnd,
  controls,
  controlsLeft,
  topBar,
  submitLabel = "Send",
  canSubmit,
  inputExtras,
  onPasteFiles,
  onDropFiles,
  hideComposer = false,
  onStop,
  stopTestId,
  onClear,
  canClear = true,
  clearConfirm,
  scrollRef,
  className,
  bodyClassName,
  optionsMenuLabel = "Chat options",
  stopLabel = "Stop",
  dockCloseLabel = "Hide chat",
  "data-testid": dataTestId,
  inputTestId,
  sendTestId,
  clearTestId
}: TChatShellProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isLeo = appearance === "leo";
  const closeDock = useDockedChatClose();
  const isComposerDisabled = isComposerDisabledProp || isLoading;

  const isSubmitDisabled = isBusy || isComposerDisabled || !(canSubmit ?? input.trim().length > 0);
  const [isDragging, setIsDragging] = useState(false);

  const clearMenuLabel = clearConfirm?.menuLabel ?? "Clear";
  const clearTitle = clearConfirm?.title ?? "Clear this chat?";
  const clearCancelText = clearConfirm?.cancelText ?? "Cancel";
  const clearConfirmText = clearConfirm?.confirmText ?? "Clear";

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterSubmit(e)) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files);
    if (!onPasteFiles || files.length === 0 || e.clipboardData.getData("text/plain")) return;
    e.preventDefault();
    onPasteFiles(files);
  };

  const dropAllowed = !!onDropFiles && !isBusy && !isComposerDisabled && !hideComposer;
  const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

  const handleDragOver = (e: DragEvent<HTMLElement>) => {
    if (!dropAllowed || !carriesFiles(e)) return;
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    setIsDragging(false);
    if (!dropAllowed || !carriesFiles(e)) return;
    e.preventDefault();
    onDropFiles?.(Array.from(e.dataTransfer.files));
  };

  const handleConfirmClear = () => {
    onClear?.();
    setShowClearConfirm(false);
    toast.success(clearConfirm?.toastText ?? "Chat cleared", { position: "top-right" });
  };

  const stopButton = (
    <AppButton
      size="sm"
      type="button"
      endAdornment={<StopIcon weight="fill" />}
      onClick={onStop}
      data-testid={stopTestId}
    >
      {stopLabel}
    </AppButton>
  );

  const sendButton = (
    <AppButton
      size="sm"
      type="button"
      endAdornment={<KeyReturnIcon />}
      disabled={isSubmitDisabled}
      onClick={onSubmit}
      data-testid={sendTestId}
    >
      {submitLabel}
    </AppButton>
  );

  return (
    <ChatAppearanceProvider value={appearance}>
    <aside
      data-testid={dataTestId}
      className={cn("flex shrink-0 flex-col justify-between", isLeo ? "bg-card" : "bg-white", className)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          data-chat-header
          className={cn(
            "flex h-14 shrink-0 items-center justify-between border-b",
            isLeo
              ? cn("gap-2 ps-4 pe-3 transition-colors duration-200", isScrolled ? "border-border" : "border-transparent")
              : "px-2 border-innoveraZinc-100"
          )}
        >
          {isLeo ? (
            <div className="flex items-center gap-2">
              <LeoWordmark className="h-4 text-leo" title={title} />
              {titleAdornment}
            </div>
          ) : (
            <div className="flex items-center gap-1 px-3 py-2 h-8 rounded-lg">
              <SparkleIcon className="size-4 text-innoveraZinc-500" weight="regular" />
              <FigmaTypography component="span" variant="label-2" color="zinc500">
                {title}
              </FigmaTypography>
              {titleAdornment}
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {headerRight}
              {onClear && isLeo && (
                <DsDropdownMenu>
                  <DsDropdownMenuTrigger
                    data-testid="chat-options-trigger"
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        shape="pill"
                        type="button"
                        aria-label={optionsMenuLabel}
                        title={optionsMenuLabel}
                      />
                    }
                  >
                    <DotsThreeVerticalIcon className="size-5" />
                  </DsDropdownMenuTrigger>
                  <DsDropdownMenuContent align="end" sideOffset={8} className="w-[161px] min-w-48 rounded-xl p-1.5">
                    <DsDropdownMenuItem
                      disabled={!canClear}
                      onClick={() => setShowClearConfirm(true)}
                      className="flex items-center gap-1.5"
                      data-testid={clearTestId}
                    >
                      <TrashSimpleIcon className="size-4 text-innoveraRed-400" />
                      <FigmaTypography variant="label-2" className="text-innoveraRed-400">
                        {clearMenuLabel}
                      </FigmaTypography>
                    </DsDropdownMenuItem>
                  </DsDropdownMenuContent>
                </DsDropdownMenu>
              )}
              {onClear && !isLeo && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-testid="chat-options-trigger">
                    <AppButton
                      appearance="text"
                      color="zinc950"
                      size="icon"
                      type="button"
                      aria-label={optionsMenuLabel}
                      title={optionsMenuLabel}
                      startAdornment={<DotsThreeVerticalIcon className="size-5" />}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[161px] p-1 border-innoveraZinc-100">
                    <DropdownMenuItem
                      disabled={!canClear}
                      onSelect={() => setShowClearConfirm(true)}
                      className="px-3 py-2 flex items-center gap-1.5"
                      data-testid={clearTestId}
                    >
                      <TrashSimpleIcon className="size-4 text-innoveraRed-400" />
                      <FigmaTypography variant="label-2" className="text-innoveraRed-400">
                        {clearMenuLabel}
                      </FigmaTypography>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {closeDock && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  type="button"
                  aria-label={dockCloseLabel}
                  title={dockCloseLabel}
                  className="rounded-full"
                  onClick={closeDock}
                  data-testid="chat-dock-close"
                >
                  <XIcon />
                </Button>
              )}
            </div>
            {headerEnd}
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={isLeo ? (e) => setIsScrolled(e.currentTarget.scrollTop > 0) : undefined}
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto",
            isLeo ? "gap-6 px-4 py-4" : "px-4 pt-1 pb-4",
            bodyClassName
          )}
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center gap-2" data-testid="chat-loading">
              <Spinner aria-label={loadingLabel} className="size-5 text-muted-foreground" />
              <FigmaTypography variant="body-2" color="zinc600">
                {loadingLabel}
              </FigmaTypography>
            </div>
          ) : (
            children
          )}
        </div>
      </div>

      {!hideComposer && isLeo && (
        <div className="px-4 pb-4">
          {topBar}
          {onClear && showClearConfirm && (
            <div
              className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-destructive/10 py-2 ps-4 pe-2"
              data-testid="clear-chat-confirm"
            >
              <div className="flex min-w-0 items-center gap-2">
                <TrashSimpleIcon className="size-4 shrink-0 text-destructive" />
                <span className="typo-label-md truncate">{clearTitle}</span>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  shape="pill"
                  onClick={() => setShowClearConfirm(false)}
                  data-testid="clear-chat-cancel"
                >
                  {clearCancelText}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  shape="pill"
                  onClick={handleConfirmClear}
                  data-testid="clear-chat-confirm-button"
                >
                  {clearConfirmText}
                </Button>
              </div>
            </div>
          )}
          <InputGroup
            className={cn(
              "rounded-3xl bg-background shadow-xs [--color-ring:var(--color-grayscale-300)]",
              isDragging && "border-dashed border-ring"
            )}
          >
            {inputExtras && (
              <InputGroupAddon align="block-start" className="px-3 pt-3">
                {inputExtras}
              </InputGroupAddon>
            )}
            <InputGroupTextarea
              ref={inputRef}
              aria-label={placeholder}
              placeholder={placeholder}
              rows={1}
              value={input}
              onChange={(e) => onInputChange(normalizeDictation(e.target.value))}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={isBusy || isComposerDisabled}
              data-testid={inputTestId}
              className="typo-body-sm field-sizing-content max-h-48 min-h-12 resize-none px-4 pt-3.5"
            />
            <InputGroupAddon
              align="block-end"
              className={cn("px-3 pb-3", controlsLeft ? "justify-between" : "justify-end")}
            >
              {controlsLeft && <div className="flex items-center gap-2">{controlsLeft}</div>}
              <div className="flex items-center gap-2">
                {controls}
                {isBusy && onStop ? (
                  <InputGroupButton
                    size="icon-sm"
                    variant="default"
                    aria-label={stopLabel}
                    className="rounded-full"
                    onClick={onStop}
                    data-testid={stopTestId}
                  >
                    <StopIcon weight="fill" className="size-3.5" />
                  </InputGroupButton>
                ) : (
                  <InputGroupButton
                    size="icon-sm"
                    variant="default"
                    aria-label={submitLabel}
                    className="rounded-full"
                    disabled={isSubmitDisabled}
                    onClick={onSubmit}
                    data-testid={sendTestId}
                  >
                    <ArrowUpIcon weight="bold" />
                  </InputGroupButton>
                )}
              </div>
            </InputGroupAddon>
          </InputGroup>
          {(footnote || (isBusy && busyNotice)) && (
            <p className="typo-caption pt-2 text-center text-muted-foreground">
              <span aria-live="polite">{isBusy && busyNotice}</span>
              {!(isBusy && busyNotice) && footnote}
            </p>
          )}
        </div>
      )}

      {!hideComposer && !isLeo && (
        <div className="p-[8px]">
          {topBar}
          {onClear && showClearConfirm && (
            <div
              className="flex items-center justify-between gap-2 mb-2 px-4 py-[11px] rounded-[8px] bg-innoveraRed-50"
              data-testid="clear-chat-confirm"
            >
              <div className="flex items-center gap-2">
                <TrashSimpleIcon className="size-4 text-innoveraRed-400" />
                <FigmaTypography variant="label-2" color="zinc900">
                  {clearTitle}
                </FigmaTypography>
              </div>
              <div className="flex items-center gap-2">
                <AppButton
                  appearance="outlined"
                  color="zinc150"
                  className="bg-white"
                  type="button"
                  onClick={() => setShowClearConfirm(false)}
                  data-testid="clear-chat-cancel"
                >
                  {clearCancelText}
                </AppButton>
                <AppButton
                  color="red400"
                  type="button"
                  onClick={handleConfirmClear}
                  data-testid="clear-chat-confirm-button"
                >
                  {clearConfirmText}
                </AppButton>
              </div>
            </div>
          )}
          <div
            className={cn(
              "flex flex-col gap-2 py-1 relative border border-innoveraZinc-150 rounded-[8px] bg-white",
              isDragging && "border-dashed border-innoveraZinc-500"
            )}
          >
            {inputExtras}
            <AppTextarea
              ref={inputRef}
              fullWidth
              className="h-[80px] resize-none border-0! ring-0!"
              placeholder={placeholder}
              rows={5}
              value={input}
              onChange={(e) => onInputChange(normalizeDictation(e.target.value))}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={isBusy || isComposerDisabled}
              data-testid={inputTestId}
            />
            <div className={cn("flex gap-2 items-center px-2 pb-1", controlsLeft ? "justify-between" : "justify-end")}>
              {controlsLeft && <div className="flex items-center gap-2">{controlsLeft}</div>}
              <div className="flex gap-2 items-center">
                {controls}
                {isBusy && onStop ? stopButton : sendButton}
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
    </ChatAppearanceProvider>
  );
}
